import React, { Component } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Header from '../layout/header';
import Footer from '../layout/footer';

import {
  PremiumHero,
  StickyMobileCTA,
  SectionWrapper,
  FaqAccordion,
  FinalCTA,
  Breadcrumbs,
  StructuredData,
} from '../elements/premium';

import keywordPages from '../../data/keyword_pages.json';
import keywordCategories from '../../data/keyword_categories.json';
import { cities as citiesData } from '../elements/cities-data';
import brandsData from '../../data/brands_data.json';
import { PRICING, startingPriceLabel, formatInr } from '../../utils/pricing';

// ─── HUB DEFINITIONS ────────────────────────────────────────────────
// All prices come from pricing-snapshot.json (backend SoT). The literal labels
// below resolve at module-load time using values from the snapshot, so any
// backend price change → next build → all hubs updated.
const BIKE_FROM = startingPriceLabel('bike');
const CAR_FROM  = startingPriceLabel('car');
const AC_FROM   = startingPriceLabel('ac');
const SITE_FROM = startingPriceLabel('site');
const AC_TIERS  = (PRICING.car_ac && PRICING.car_ac.tiers) || [];
const AC_TIER_SENTENCE = AC_TIERS.length
  ? AC_TIERS.map((t) => `${formatInr(t.price)} for ${t.label.toLowerCase()}`).join(', ')
  : `${AC_FROM} for hatchbacks`;

const HUBS = {
  'bike-services': {
    title: 'Bike Services',
    metaTitle: 'Bike Service & Repair — Doorstep Two-Wheeler Service | Ride N Repair',
    metaDescription: `Complete doorstep bike service and repair across 32+ Indian cities. Oil change, brake repair, engine service, tyre replacement, and more. Starting ${BIKE_FROM}.`,
    heroTitle: 'Doorstep Bike Service & Repair',
    heroSub: 'Oil change · Brake repair · Engine service · Tyre replacement · 30-day warranty',
    priceTag: `Starting ${BIKE_FROM}`,
    categories: ['bike-general', 'bike-repair', 'bike-engine', 'bike-brake', 'bike-tyre', 'bike-battery', 'bike-clutch', 'bike-suspension', 'bike-electrical', 'bike-painting', 'bike-emergency'],
    vehicleType: 'bike',
    faqs: [
      { q: 'What bike services does Ride N Repair offer?', a: 'We offer general service, oil change, brake repair, engine repair, tyre replacement, battery replacement, clutch repair, electrical work, painting, and emergency roadside assistance for all two-wheeler brands.' },
      { q: 'How much does a bike service cost?', a: `Bike services start from ${BIKE_FROM}. Prices vary by service type and bike model. All pricing is transparent — you see the exact cost before booking.` },
      { q: 'Do you service all bike brands?', a: 'Yes! We service Hero, Honda, Bajaj, TVS, Royal Enfield, Yamaha, Suzuki, KTM, Jawa, and 20+ more brands.' },
      { q: 'Is doorstep bike service available in my city?', a: 'Ride N Repair operates in 32+ cities across India including Bengaluru, Hyderabad, Delhi NCR, Mumbai, Pune, Chennai, Kolkata, and more.' },
    ],
  },
  'car-services': {
    title: 'Car Services',
    metaTitle: 'Car Service & Repair — Doorstep Car Service | Ride N Repair',
    metaDescription: `Comprehensive doorstep car service and repair across 32+ Indian cities. General service, AC repair, brake service, engine repair, and more. Starting ${CAR_FROM}.`,
    heroTitle: 'Doorstep Car Service & Repair',
    heroSub: 'General service · AC repair · Brake service · Engine repair · 30-day warranty',
    priceTag: `Starting ${CAR_FROM}`,
    categories: ['car-general', 'car-repair', 'car-ac', 'car-engine', 'car-brake', 'car-tyre', 'car-battery', 'car-clutch', 'car-suspension', 'car-painting', 'car-emergency'],
    vehicleType: 'car',
    faqs: [
      { q: 'What car services does Ride N Repair offer?', a: 'We offer general service, AC repair, engine diagnostics, brake service, tyre replacement, battery replacement, clutch repair, suspension work, denting & painting, and emergency assistance for all car brands.' },
      { q: 'How much does a car service cost?', a: `Car services start from ${CAR_FROM}. Prices vary by service type and car model. All pricing is transparent with no hidden charges.` },
      { q: 'Which car brands do you service?', a: 'We service Maruti, Hyundai, Tata, Mahindra, Kia, Toyota, Honda, MG, Volkswagen, BMW, Mercedes, Audi, and 30+ more brands.' },
      { q: 'Is doorstep car service available in my city?', a: 'Ride N Repair operates in 32+ cities across India. Check our app for availability in your specific area.' },
    ],
  },
  'ac-repair-services': {
    title: 'AC Repair Services',
    metaTitle: 'Car AC Repair & Service — Doorstep AC Service | Ride N Repair',
    metaDescription: `Professional car AC repair and service at your doorstep. 12-point inspection, gas refill, condenser cleaning, 30-day cooling warranty. Starting ${AC_FROM}.`,
    heroTitle: 'Car AC Repair & Service',
    heroSub: '12-point inspection · Gas refill · Condenser cleaning · 30-day cooling warranty',
    priceTag: `Starting ${AC_FROM}`,
    categories: ['car-ac'],
    vehicleType: 'car',
    faqs: [
      { q: 'What does the AC service include?', a: 'Our 12-point service covers condenser cleaning, vent cleaning, leak detection, compressor check, blower motor inspection, cabin filter check, and a post-service cooling test.' },
      { q: 'How much does AC repair cost?', a: `AC service starts at ${AC_TIER_SENTENCE}.` },
      { q: 'Is AC gas refill included?', a: 'Gas refill is available as an add-on, billed separately based on the quantity of refrigerant required.' },
      { q: 'What warranty do you provide?', a: 'Every AC service comes with a 30-day cooling performance guarantee. If cooling drops within 30 days, we fix it free.' },
    ],
  },
  'battery-services': {
    title: 'Battery Services',
    metaTitle: 'Car & Bike Battery Replacement — Doorstep Service | Ride N Repair',
    metaDescription: `Doorstep battery replacement for cars and bikes. Exide, Amaron, and more. Jump-start service, old battery buyback. Starting ${CAR_FROM}.`,
    heroTitle: 'Battery Replacement & Jump Start',
    heroSub: 'Car & bike batteries · Jump-start service · Old battery buyback · Manufacturer warranty',
    priceTag: `Starting ${CAR_FROM}`,
    categories: ['car-battery', 'bike-battery'],
    vehicleType: 'both',
    faqs: [
      { q: 'Which battery brands do you carry?', a: 'We carry batteries from Exide, Amaron, Luminous, and other trusted brands with manufacturer warranty up to 5 years.' },
      { q: 'Do you buy back old batteries?', a: 'Yes! We offer an exchange discount when you trade in your old battery during replacement.' },
      { q: 'How quickly can you replace my battery?', a: 'Battery replacement takes 15-30 minutes. Our mechanic arrives within 15 minutes in most cities.' },
      { q: 'Do you offer jump-start service?', a: 'Yes, we provide emergency jump-start service for dead batteries across all cities.' },
    ],
  },
  'brake-services': {
    title: 'Brake Services',
    metaTitle: 'Brake Pad Replacement & Repair — Doorstep Service | Ride N Repair',
    metaDescription: `Professional brake pad replacement, disc repair, and brake fluid change for cars and bikes. Doorstep service with 30-day warranty. Starting ${BIKE_FROM}.`,
    heroTitle: 'Brake Repair & Replacement',
    heroSub: 'Brake pads · Disc repair · Fluid change · ABS diagnostics · 30-day warranty',
    priceTag: `Starting ${BIKE_FROM}`,
    categories: ['car-brake', 'bike-brake'],
    vehicleType: 'both',
    faqs: [
      { q: 'How much does brake pad replacement cost?', a: `Brake repair starts from ${BIKE_FROM} for bikes and ${CAR_FROM} for cars. Prices vary by vehicle model.` },
      { q: 'How do I know if my brakes need replacement?', a: 'Signs include squealing noise, longer stopping distance, vibration when braking, or the brake warning light on your dashboard.' },
      { q: 'Do you service ABS brakes?', a: 'Yes, our mechanics are trained in ABS diagnostics and service for both cars and bikes.' },
      { q: 'What brands of brake pads do you use?', a: 'We use genuine OEM brake pads from manufacturers like Bosch, Brembo, and brand-specific originals.' },
    ],
  },
  'tyre-services': {
    title: 'Tyre Services',
    metaTitle: 'Tyre Replacement & Puncture Repair — Doorstep Service | Ride N Repair',
    metaDescription: `Doorstep tyre replacement, puncture repair, wheel alignment, and balancing for cars and bikes. MRF, CEAT, Apollo, Bridgestone. Starting ${BIKE_FROM}.`,
    heroTitle: 'Tyre Replacement & Puncture Repair',
    heroSub: 'New tyres · Puncture repair · Wheel alignment · Balancing · All brands',
    priceTag: `Starting ${BIKE_FROM}`,
    categories: ['car-tyre', 'bike-tyre'],
    vehicleType: 'both',
    faqs: [
      { q: 'Which tyre brands do you carry?', a: 'We carry tyres from MRF, CEAT, Apollo, Bridgestone, Michelin, TVS, and more.' },
      { q: 'How much does a puncture repair cost?', a: `Bike puncture repair starts from ${formatInr((PRICING.bike && PRICING.bike.services && PRICING.bike.services.puncture && PRICING.bike.services.puncture.price) || 600)}; car puncture from ${formatInr((PRICING.car && PRICING.car.puncture) || 999)}.` },
      { q: 'Do you provide wheel alignment?', a: 'Yes, we offer computerised wheel alignment and dynamic wheel balancing at your doorstep.' },
      { q: 'Can you replace my tyre at home?', a: 'Absolutely! Our mechanic arrives with the right tyre for your vehicle and replaces it on-spot.' },
    ],
  },
  'doorstep-services': {
    title: 'Doorstep Services',
    metaTitle: 'Doorstep Vehicle Service — Bike & Car Service at Home | Ride N Repair',
    metaDescription: 'Get your bike or car serviced at your doorstep. No garage visits, no waiting. Certified mechanics, genuine parts, 30-day warranty. 32+ cities.',
    heroTitle: 'Doorstep Vehicle Service',
    heroSub: 'Bike & car service at your home or office · No garage visit needed',
    priceTag: `Starting ${SITE_FROM}`,
    categories: ['bike-general', 'car-general', 'bike-repair', 'car-repair'],
    vehicleType: 'both',
    faqs: [
      { q: 'How does doorstep service work?', a: 'Book online in 60 seconds, our certified mechanic arrives at your chosen location with all tools and parts. Service is done right there — no need to visit a garage.' },
      { q: 'Where can I get doorstep service?', a: 'We serve at your home, office, parking lot, or any convenient location in our 32+ service cities.' },
      { q: 'Is doorstep service as good as garage service?', a: 'Our mechanics are certified and equipped with professional tools. For complex work that needs a lift, we offer free towing to our partner workshop and delivery after service.' },
      { q: 'What if the mechanic can\'t fix my vehicle on-spot?', a: 'For complex repairs, we provide free towing to our partner workshop. You get transparent updates throughout and the vehicle is delivered back to you washed and serviced.' },
    ],
  },
  'emergency-repair': {
    title: 'Emergency Repair',
    metaTitle: 'Emergency Vehicle Repair & Roadside Assistance | Ride N Repair',
    metaDescription: 'Emergency bike and car repair, roadside assistance, jump-start, towing, and flat tyre repair. Available across 32+ cities. Mechanics arrive within 15 min.',
    heroTitle: 'Emergency Repair & Roadside Assistance',
    heroSub: 'Breakdown repair · Jump-start · Flat tyre · Towing · Available 24/7',
    priceTag: `Starting ${SITE_FROM}`,
    categories: ['bike-emergency', 'car-emergency'],
    vehicleType: 'both',
    faqs: [
      { q: 'How quickly do you respond to emergencies?', a: 'Our mechanics arrive within 15 minutes in most cities. Emergency services are prioritized over regular bookings.' },
      { q: 'What emergency services do you offer?', a: 'We offer roadside breakdown repair, battery jump-start, flat tyre repair/replacement, emergency towing, fuel delivery, and lockout assistance.' },
      { q: 'Is emergency service available 24/7?', a: 'Yes, our emergency helpline and service are available around the clock across all service cities.' },
      { q: 'How much does emergency service cost?', a: `Emergency call-out starts from ${SITE_FROM}. Actual repair costs depend on the issue. Pricing is confirmed before any work begins.` },
    ],
  },
};

class CategoryHub extends Component {
  render() {
    const hubSlug = this.props.hubSlug;
    const hub = HUBS[hubSlug];

    if (!hub) return <Redirect to="/error-404" />;

    const {
      isMobileBrowser, isMobileApp, user, setUser, setCitiesModal, blogsData,
    } = this.props;

    const canonical = `https://www.ridenrepair.com/${hubSlug}`;

    // Collect keyword links for this hub's categories
    const keywordLinks = [];
    Object.entries(keywordCategories).forEach(([slug, meta]) => {
      if (hub.categories.includes(meta.category) && keywordPages[slug]) {
        keywordLinks.push({ slug, title: keywordPages[slug] });
      }
    });
    // Limit to top 60 for display, prioritize "near-me" keywords
    keywordLinks.sort((a, b) => {
      const aScore = a.slug.includes('near-me') ? 0 : a.slug.includes('cost') ? 1 : a.slug.includes('at-home') ? 2 : 3;
      const bScore = b.slug.includes('near-me') ? 0 : b.slug.includes('cost') ? 1 : b.slug.includes('at-home') ? 2 : 3;
      return aScore - bScore || a.title.localeCompare(b.title);
    });
    const displayedLinks = keywordLinks.slice(0, 60);

    // City links
    const cities = citiesData || [];

    // Related blogs
    const relatedBlogs = (blogsData || []).slice(0, 4);

    // Schema
    const schemas = [
      {
        '@type': 'ItemList',
        name: hub.title,
        description: hub.metaDescription,
        numberOfItems: keywordLinks.length,
        itemListElement: displayedLinks.slice(0, 20).map((kw, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: kw.title,
          url: `https://www.ridenrepair.com/${kw.slug}`,
        })),
      },
    ];

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>{hub.metaTitle}</title>
          <meta name="description" content={hub.metaDescription} />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={hub.metaTitle} />
          <meta property="og:description" content={hub.metaDescription} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={canonical} />
        </Helmet>

        <StructuredData data={schemas} />

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        <PremiumHero
          compact
          badge={hub.title}
          title={hub.heroTitle}
          priceTag={hub.priceTag}
          subtitle={hub.heroSub}
          isMobileBrowser={isMobileBrowser}
          showCarAnimation
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'secondary' },
          ]}
        />

        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: hub.title }]} />

        <StickyMobileCTA title={hub.title} priceHint={`${hub.priceTag} · 30-Day Warranty`} btnLabel="Book Now" btnHref="/service" />

        {/* SERVICE LINKS GRID */}
        <SectionWrapper kicker="Our Services" heading={`${hub.title} — Browse All`} subtitle={`Find the exact service you need. ${keywordLinks.length} services available across 32+ cities.`}>
          <div className="rr-related-links">
            {displayedLinks.map((kw) => (
              <Link key={kw.slug} to={`/${kw.slug}`} className="rr-related-link">
                <div className="rr-related-link__title">{kw.title}</div>
                <div className="rr-related-link__desc">Doorstep service · Starting {hub.priceTag}</div>
              </Link>
            ))}
          </div>
          {keywordLinks.length > 60 && (
            <p className="rr-text-center rr-mt-32" style={{ color: '#999', fontSize: 14 }}>
              Showing top 60 of {keywordLinks.length} services. Use search or browse by city below.
            </p>
          )}
        </SectionWrapper>

        {/* BRANDS */}
        <SectionWrapper variant="gray" kicker="Brands" heading={`${hub.title} — Brands We Service`}>
          <div className="rr-brand-grid">
            {(() => {
              const vt = hub.vehicleType;
              let pool = [];
              if (vt === 'bike' || vt === 'both') pool.push(...(brandsData.bike || []));
              if (vt === 'car' || vt === 'both') pool.push(...(brandsData.car || []));
              const seen = new Set();
              return pool
                .filter(b => { if (seen.has(b.slug)) return false; seen.add(b.slug); return true; })
                .slice(0, 24)
                .map(b => (
                  <Link key={b.slug} to={`/brands/${b.slug}`} className="rr-brand-card">
                    {(() => {
                      try {
                        const logo = require(`../../images/brand_logos/${b.logo}`);
                        return <img src={typeof logo === 'object' ? logo.default : logo} alt={b.label} className="rr-brand-card__logo" loading="lazy" />;
                      } catch (e) {
                        return <div className="rr-brand-card__logo" style={{ fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{b.label.charAt(0)}</div>;
                      }
                    })()}
                    <span className="rr-brand-card__label">{b.label}</span>
                    <span className="rr-brand-card__count">{b.models.length} models</span>
                  </Link>
                ));
            })()}
          </div>
          <div className="rr-text-center rr-mt-32">
            <Link to="/brands" className="rr-cta-dark-outline">View All Brands</Link>
          </div>
        </SectionWrapper>

        {/* CITIES */}
        <SectionWrapper kicker="Available In" heading={`${hub.title} — Cities We Serve`}>
          <div className="rr-city-grid">
            {cities.map((city) => (
              <Link key={city.key} to={`/city/${city.key}`} className="rr-city-chip">
                {city.name}
              </Link>
            ))}
          </div>
        </SectionWrapper>

        {/* FAQ */}
        <SectionWrapper kicker="FAQ" heading={`${hub.title} — Common Questions`}>
          <FaqAccordion faqs={hub.faqs} />
        </SectionWrapper>

        {/* RELATED BLOGS */}
        {relatedBlogs.length > 0 && (
          <SectionWrapper variant="gray" kicker="From Our Blog" heading="Helpful Articles">
            <div className="rr-related-links">
              {relatedBlogs.map((blog) => (
                <Link key={blog.slug} to={`/blog-details/${blog.slug}`} className="rr-related-link">
                  <div className="rr-related-link__title">{blog.title}</div>
                  <div className="rr-related-link__desc">{blog.category || 'Blog'}</div>
                </Link>
              ))}
            </div>
          </SectionWrapper>
        )}

        {/* FINAL CTA */}
        <FinalCTA
          heading={<>Need {hub.title}?<br />Book Now — Get It Done Today.</>}
          subtitle={`Doorstep service · Certified mechanics · ${hub.priceTag} · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export { HUBS };
export default CategoryHub;
